import { useEffect, useState } from "react";
import { Loader2, MapPin, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export function KioskAccessCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/kiosk/access").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/kiosk/access/settings", {
        kiosk_geofence: !!cfg.geofence_enabled, kiosk_geofence_radius_m: Number(cfg.radius_m) || 300,
      });
      setCfg(data);
      toast.success("Kiosk access saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!cfg) return null;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="kiosk-access-card">
      <div className="mb-1.5 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-primary" />
        <span className="text-xs font-medium uppercase tracking-widest text-slate-500">Kiosk access</span>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        The kiosk can only be opened by admins and officers. With on-site verification on, the device's location is
        checked against this location's address (verified with Google) before the kiosk loads.
      </p>
      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-4">
        <div className="max-w-md">
          <div className="text-sm font-semibold text-slate-900">Require kiosk to be on-site</div>
          <div className="mt-0.5 text-sm text-slate-600">
            {cfg.address ? `Checked against: ${cfg.address}` : "Add and verify this location's address above first."}
          </div>
        </div>
        <button type="button" role="switch" aria-checked={!!cfg.geofence_enabled} data-testid="kiosk-geofence-toggle"
                onClick={() => setCfg({ ...cfg, geofence_enabled: !cfg.geofence_enabled })}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${cfg.geofence_enabled ? "bg-primary" : "bg-slate-300"}`}>
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${cfg.geofence_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-slate-500">Allowed radius</span>
          <span className="inline-flex items-center gap-2 text-sm">
            <input type="number" min={50} max={5000} step={50} value={cfg.radius_m}
                   onChange={(e) => setCfg({ ...cfg, radius_m: e.target.value })} data-testid="kiosk-geofence-radius"
                   className="h-10 w-24 rounded-lg border border-slate-200 px-3 text-right text-sm" /> meters
          </span>
        </label>
        <button type="button" onClick={save} disabled={saving} data-testid="kiosk-access-save"
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save kiosk access
        </button>
      </div>
    </section>
  );
}
